/**
 * Compass Explorer — scene builder.
 *
 * Takes the folder graph + current focus + zoom, and produces a
 * positioned `Scene` the existing canvas renderer can consume. Pure
 * function. No persistence, no Yjs, no server round-trip.
 *
 * Pipeline:
 *   graph  ──► sliceForFocus(graph, focus, zoom)  ──► subgraph
 *   subgraph ──► InfraLayoutEngine.layout()        ──► positions
 *   positions + subgraph ──► Scene { nodes, edges }
 *
 * Bubble nodes (L1/L2/L3 containers, derived L4, overview L5) are laid
 * out in a simple grid; focused leaf nodes go through the full
 * InfraLayoutEngine path so stacks, tier-groups, clusters, and
 * shared-chips all work.
 */

import type { Scene, Node, Edge } from '../../../core/models/scene';
import type { PersistedGraph } from '../models/KnowledgeGraph';
import type { FocusSpec, ZoomLevel } from '../../../stores/compassViewPrefsStore';
import {
  InfraLayoutEngine,
  classifyInfraTier,
  classifyEdgeTier,
  type InfraLayoutEdge,
  type InfraLayoutLeaf,
  type InfraLayoutContainer,
} from './InfraLayoutEngine';
import {
  PatternRecognizer,
  type MatchedPattern,
} from './PatternRecognizer';
import { runElkInfraLayout } from './layoutEnhancers/elkInfraLayout';
import { routeEdges as routeOrthogonalEdges } from '../../../core/routing/routeEdges';
import { sliceForFocus, type BubbleNode } from './explorer/focusFilter';
import { attributeMonthlyCosts } from './explorer/costAttribution';
import { attributeFindings, type FindingSeverity } from './explorer/findingAttribution';
import { nodePassesActiveChips } from './explorer/filterChips';
import { computeBubbleTopology } from './explorer/bubbleTopology';
import { FindingsRegistry } from './explorer/findings';
import { groupBubblesByEnv } from './explorer/envGrouping';
import { clusterBubbles } from './explorer/BubbleClusterer';
import { groupByTier } from '../canvas/tierGrouping';
import {
  CARD_BAND_GAP,
  cardBandHeight,
  cardDotsPerRow,
  cardHeaderHeight,
} from '../canvas/renderers/CompassContainerCard';

const BUBBLE_GAP = 40;
const ORIGIN = { x: 80, y: 80 };
const BUBBLE_EDGE_COLOR = '#2563eb';
const BUBBLE_EDGE_MUTED_COLOR = '#5b6b85';
const BUBBLE_EDGE_ACTIVE_COLOR = '#2a73e4';
const BUBBLE_EDGE_ARROW = { type: 'chevron', size: 9 } as const;

const layoutEngine = new InfraLayoutEngine();
const patternRecognizer = new PatternRecognizer();
const GENERIC_MANIFEST_IDS = new Set(['aws_generic', 'generic', 'resource']);

function isCompassRouterDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem('compass.debugRouter') === '1';
  } catch {
    return false;
  }
}

function manifestIdOf(node: {
  type: string;
  props?: unknown;
}): string {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const mid = (props._manifestId as string | undefined) ?? (props.manifestId as string | undefined);
  const propResourceType = typeof props.resourceType === 'string' ? props.resourceType : undefined;
  if (propResourceType && (!mid || GENERIC_MANIFEST_IDS.has(mid))) {
    return `aws_${propResourceType.replace(/-/g, '_').toLowerCase()}`;
  }
  if (mid) return String(mid);
  // Current ingest path stores the manifest id in node.type directly
  // (e.g. 'aws_vpc'). Keep as-is.
  if (node.type && node.type.startsWith('aws_')) return node.type;
  // Last resort: treat type as the manifest id. Engine classifier
  // falls back to `other` tier which is still renderable.
  return node.type;
}

function resourceTypeOf(manifestId: string): string {
  return manifestId.startsWith('aws_') ? manifestId.slice(4) : manifestId;
}

export interface BuildSceneOptions {
  /** Default shape dimensions when manifest lookup fails. */
  defaultShapeSize?: { width: number; height: number };
  /**
   * Ordered list of frame / bubble ids the user has expanded in place.
   * Expanded shapes get `attrs.expanded=true`; when the set is
   * non-empty, siblings get `attrs.dimmed=true` so unrelated context
   * fades out. The scene builder does not currently change layout
   * based on this set — Phase 3 wires up the state plumbing and visual
   * affordance; a follow-up iteration replaces the dot-grid card with
   * expanded inner content.
   */
  expandedFrameIds?: readonly string[];
  /**
   * Ids of aggregated cross-subnet edges that should be expanded to
   * their constituent leaf-to-leaf edges. When an aggregated edge id
   * appears in this set its members render individually and the
   * aggregate itself is suppressed.
   */
  expandedEdgeIds?: readonly string[];
  /**
   * Node ids currently selected. Selection auto-expands any
   * aggregated edge that touches a selected node — selecting a
   * subnet frame reveals the actual connections flowing from it,
   * selecting a leaf reveals its enclosing subnet's aggregates.
   * Merged with the explicit `expandedEdgeIds` set so manual clicks
   * still work.
   */
  selectedIds?: readonly string[];
  /**
   * When `false`, skip cross-subnet edge aggregation entirely and
   * render every leaf-to-leaf connection. Default `true`.
   */
  collapseEdges?: boolean;
  /**
   * P0.1 compare mode — stamp `attrs.delta` on each node to reflect
   * add/remove/change vs this older version. Scene layout is unchanged;
   * renderers tint based on `attrs.delta` only. When omitted, no delta
   * is stamped. The diff itself is computed by `ExplorerViewService`
   * and passed in via a pre-populated attrs map rather than re-read
   * here, to keep this function pure.
   */
  previousVersion?: number;
  /**
   * P1.3 active lens id. The lens decoration is applied post-layout;
   * stamped as `attrs.lens = { color?, badge?, icon?, dataMissing? }`.
   * Renderers branch on `attrs.lens` only — not on lensId — so this
   * stays strategy-pattern clean.
   */
  lensId?: string;
  /**
   * P1.4 active scenario id — when set, the caller is expected to have
   * already merged the scenario's proposed resources into `graph`. The
   * scene builder simply stamps `attrs.scenarioId` + `attrs.provisional`
   * on nodes carrying a `provisional` prop so renderers can dash them.
   */
  activeScenarioId?: string;
  /**
   * Per-resource estimated monthly USD spend, attributed from service-
   * level Cost Explorer findings. When present the scene stamps each
   * leaf with `attrs.monthlySpend` so the "Cost heatmap" preset has
   * data to gradient against at L4/L5. Empty / undefined → no stamp.
   */
  monthlySpendById?: ReadonlyMap<string, number>;
  /**
   * Per-resource top finding severity (security / policy / drift).
   * Stamped onto each leaf's `attrs.findingSeverity` so the card
   * renderer can paint a severity dot.
   */
  findingSeverityById?: ReadonlyMap<string, FindingSeverity>;
  findingCountById?: ReadonlyMap<string, number>;
  /**
   * Compass quick-filter chip ids currently toggled on. The scene
   * stamps `attrs.filterDimmed=true` on leaves that fail the union of
   * active predicates; renderers fade them to 0.25 opacity so the
   * matching subset pops without losing context. Empty → no chip
   * filtering applied.
   */
  activeFilterChipIds?: readonly string[];
}

export async function buildExplorerScene(
  graph: PersistedGraph,
  focus: FocusSpec,
  zoom: ZoomLevel,
  options: BuildSceneOptions = {},
): Promise<Scene> {
  // 220×60 trades a little extra horizontal space for legibility on
  // real AWS names (e.g. `stage3-neutrino-alb-internal`). 180 was too
  // tight and truncated most mid-length names into ellipsis. 220 fits
  // ~28 chars at the bold 12px font before truncating, which covers
  // the long tail of standard tagging conventions.
  const defaultSize = options.defaultShapeSize ?? { width: 220, height: 60 };
  const expandedSet = new Set(options.expandedFrameIds ?? []);
  // The expanded set stored on the view prefs is keyed by bubble id
  // (`bubble:<container>`) or raw container id — normalise both to the
  // container id so the topology aggregator can check container
  // membership directly.
  const expandedContainerRefs = new Set<string>();
  for (const id of expandedSet) {
    expandedContainerRefs.add(id.startsWith('bubble:') ? id.slice('bubble:'.length) : id);
  }
  const slice = sliceForFocus(graph, focus, zoom);

  // Estimate per-resource monthly spend from service-level cost
  // findings so heatmap lenses ("Cost heatmap") have data to render
  // against at resource/config zoom. Pass the map into both scene
  // builders via options so we don't recompute per stamping site.
  const costAttribution = attributeMonthlyCosts(graph);
  // Same approach for finding severity (security / policy / drift) so
  // every leaf gets a top-left severity dot without re-walking the
  // finding nodes per render. Cost anomalies are deliberately
  // omitted — they live on the cost lens badge already.
  const findingAttribution = attributeFindings(graph);

  // sliceForFocus aggregates `monthlyCostUsd` onto each bubble from
  // its members' `props.cost`, but cost is only attributed in this
  // builder — the slicer can't see it. Walk each bubble's memberIds
  // and roll up the attributed spend so the Cost heatmap lens has
  // data to colour bubbles by at L1–L4 (resource-type / workload /
  // VPC overview). Without this every bubble shows "no cost" and
  // the lens looks broken even though leaves carry the right value.
  const SEVERITY_ORDER: Record<NonNullable<BubbleNode['findingSeverity']>, number> = {
    info: 0,
    low: 1,
    medium: 2,
    high: 3,
    critical: 4,
  };
  for (const bubble of slice.bubbleNodes) {
    const memberIds = bubble.memberIds ?? [];
    // Cost roll-up: sum attributed monthly spend across members.
    if (bubble.monthlyCostUsd === undefined) {
      let total = 0;
      let carriers = 0;
      for (const memberId of memberIds) {
        const spend = costAttribution.monthlySpendById.get(memberId);
        if (spend !== undefined && spend > 0) {
          total += spend;
          carriers++;
        }
      }
      if (carriers > 0) {
        bubble.monthlyCostUsd = total;
        bubble.costCoverage = memberIds.length > 0 ? carriers / memberIds.length : 1;
      }
    }
    // Severity roll-up: max severity + total finding count across members.
    if (bubble.findingSeverity === undefined) {
      let worst: BubbleNode['findingSeverity'] | undefined;
      let totalFindings = 0;
      for (const memberId of memberIds) {
        const sev = findingAttribution.severityById.get(memberId);
        if (!sev) continue;
        totalFindings += findingAttribution.countById.get(memberId) ?? 1;
        if (!worst || SEVERITY_ORDER[sev] > SEVERITY_ORDER[worst]) worst = sev;
      }
      if (worst) {
        bubble.findingSeverity = worst;
        bubble.findingCount = totalFindings;
      }
    }
  }
  const activeChipIds = new Set(options.activeFilterChipIds ?? []);
  const chipEvalCtx = { monthlySpendById: costAttribution.monthlySpendById };

  // When chips are active, dim each bubble that has zero matching
  // members. Bubble ids are synthetic (`bubble:foo`) so the chip
  // predicates would never match them directly — they evaluate
  // per-resource and a bubble passes if *any* of its descendants
  // pass. Without this the L4/L5-overview view stayed un-dimmed
  // even with chip filters set, making the chip UI feel inert.
  if (activeChipIds.size > 0) {
    const graphById = new Map<string, (typeof graph.nodes)[number]>();
    for (const n of graph.nodes) graphById.set(n.id, n);
    for (const bubble of slice.bubbleNodes) {
      const memberIds = bubble.memberIds ?? [];
      if (memberIds.length === 0) continue;
      let anyMatch = false;
      for (const memberId of memberIds) {
        const member = graphById.get(memberId);
        if (!member) continue;
        if (nodePassesActiveChips(member, activeChipIds, chipEvalCtx)) {
          anyMatch = true;
          break;
        }
      }
      bubble.filterDimmed = !anyMatch;
    }
  }
  const optionsWithCost: BuildSceneOptions = {
    ...options,
    monthlySpendById: costAttribution.monthlySpendById,
    findingSeverityById: findingAttribution.severityById,
    findingCountById: findingAttribution.countById,
  };

  if (focus.kind === 'vpc' && zoom === 5) {
    return buildFocusedVpcResourceScene(slice, focus.vpcId, graph, defaultSize, optionsWithCost);
  }

  // ── Bubble zoom levels: L1/L2/L3 containers, L4 workloads, L5 overview
  if (slice.bubbleNodes.length > 0) {
    if (focus.kind === 'vpc' && zoom >= 3) {
      return buildFocusedVpcBubbleScene(
        slice.bubbleNodes,
        focus.vpcId,
        graph,
        expandedSet,
        expandedContainerRefs,
      );
    }

    // Cluster long tails before placement so a 200-VPC account reads
    // as "12 big VPCs + a sandbox cluster". Super-bubbles reuse the
    // existing `CompassContainerCard` renderer — we stamp
    // `attrs.cluster` + `attrs.memberIds` later in buildBubbleScene.
    const { bubbles: visibleBubbles, clusters } = clusterBubbles(slice.bubbleNodes);
    const memberIdsByCluster = new Map<string, readonly string[]>(
      clusters.map((c) => [c.clusterId, c.memberIds]),
    );
    return buildBubbleScene(
      visibleBubbles,
      expandedSet,
      graph,
      expandedContainerRefs,
      memberIdsByCluster,
    );
  }

  // ── L3+: run the full infra layout engine over the slice ─────
  const leaves: InfraLayoutLeaf[] = slice.leafNodes.map((n) => {
    const manifestId = manifestIdOf(n);
    const rt = resourceTypeOf(manifestId);
    const props = (n.props ?? {}) as Record<string, unknown>;
    return {
      id: n.id,
      manifestId,
      resourceType: rt,
      name: String(n.label ?? manifestId),
      width: defaultSize.width,
      height: defaultSize.height,
      tags: (props.tags as Record<string, string> | undefined) ?? undefined,
      az: props.az as string | undefined,
    };
  });
  const containers: InfraLayoutContainer[] = slice.containerNodes.map((n) => {
    const manifestId = manifestIdOf(n);
    return {
      id: n.id,
      manifestId,
      resourceType: resourceTypeOf(manifestId),
      name: String(n.label ?? manifestId),
    };
  });
  const containmentEdges: InfraLayoutEdge[] = slice.containmentEdges.map((e) => ({
    id: e.id,
    source: e.from,
    target: e.to,
    kind: e.type,
  }));
  const flowLayoutEdges: InfraLayoutEdge[] = slice.flowEdges.map((e) => ({
    id: e.id,
    source: e.from,
    target: e.to,
    kind: e.type,
  }));

  const layout = layoutEngine.layout({
    leaves,
    containers,
    containmentEdges,
    flowEdges: flowLayoutEdges,
  });

  // ── ELK geometry pass ────────────────────────────────────────
  // The engine produced the *classification* (leaves → subnets →
  // tier-groups → VPCs, clusters, shared lane). Swap its grid
  // placement for an ELK nested layered layout so positions respect
  // edge connectivity. Edges are NOT routed by ELK — the default
  // orthogonal router in EdgeConnector draws them using the anchors
  // we stamp below. ELK's layered routing tends to over-cross on
  // infra diagrams; our fallback reads cleaner in practice.
  {
    const shapeSize = (id: string): { width: number; height: number } | undefined => {
      const cluster = layout.clusters.get(id);
      if (cluster) return { width: cluster.width, height: cluster.height };
      const leaf = leaves.find((l) => l.id === id);
      if (leaf) return { width: leaf.width, height: leaf.height };
      return undefined;
    };
    const rewritten = flowLayoutEdges.map((e) => ({
      ...e,
      source: layout.edgeRewrites.get(e.source) ?? e.source,
      target: layout.edgeRewrites.get(e.target) ?? e.target,
    }));
    try {
      const elkResult = await runElkInfraLayout({
        engineOutput: layout,
        flowEdges: rewritten,
        shapeSize,
      });
      if (elkResult) {
        // Merge: ELK owns *geometry* — positions + frame bounds. We
        // intentionally discard ELK's edge routes and let
        // EdgeConnector's default orthogonal router draw connectors
        // using our anchors. ELK's layered routing produced too many
        // crossings for this diagram kind; the default router +
        // good anchors reads cleaner.
        for (const [id, pos] of elkResult.positions) {
          layout.positions.set(id, pos);
        }
        for (const [id, frame] of elkResult.frames) {
          layout.frames.set(id, frame);
        }
      }
    } catch (err) {
      if (
        typeof window !== 'undefined' &&
        (window as unknown as { __COMPASS_DEBUG__?: boolean }).__COMPASS_DEBUG__
      ) {
        console.warn('[compass:buildExplorerScene] ELK layout failed', err);
      }
    }
  }

  // Build manifest + tier lookups for pattern recognition and edge tiering.
  const manifestByResourceId = new Map<string, string>();
  for (const n of [...slice.leafNodes, ...slice.containerNodes]) {
    manifestByResourceId.set(n.id, manifestIdOf(n));
  }

  // Rewrite flow edges through cluster reps; used both for pattern
  // recognition and scene output.
  const rewrittenFlow = flowLayoutEdges.map((e) => ({
    ...e,
    source: layout.edgeRewrites.get(e.source) ?? e.source,
    target: layout.edgeRewrites.get(e.target) ?? e.target,
  }));

  // ── Hierarchical edge aggregation ────────────────────────────
  // Cross-subnet leaf-to-leaf edges are collapsed into a single
  // subnet-to-subnet "aggregated" edge per ordered frame pair,
  // dropping visual edge count 5-10x on dense infra diagrams. The
  // router then draws one clean inter-subnet connector per pair
  // instead of N overlapping lanes. Intra-subnet edges (both
  // endpoints in the same subnet) stay leaf-level so the detail
  // inside each subnet still shows.
  const subnetOfShape = new Map<string, string>();
  for (const [frameId, frame] of layout.frames) {
    if (frame.resourceType !== 'subnet') continue;
    const children = layout.frameShapes.get(frameId) ?? [];
    for (const childId of children) subnetOfShape.set(childId, frameId);
  }

  interface AggregatedEdgeMeta {
    /** Canonical id used as the aggregated edge's edge-id. */
    id: string;
    srcSubnet: string;
    tgtSubnet: string;
    /** Original leaf edge ids absorbed by this aggregate. */
    memberIds: string[];
    kinds: Set<string>;
  }
  const aggregatedByPair = new Map<string, AggregatedEdgeMeta>();
  const intraSubnetFlow: typeof rewrittenFlow = [];
  const collapseEdges = options.collapseEdges !== false;
  const expandedEdgeSet = new Set(options.expandedEdgeIds ?? []);

  // Selection-driven auto-expansion. Compute the set of subnet
  // container ids that are "touched" by the selection:
  //   - A selected subnet frame (id `frame:<subnet>`) → that subnet
  //     itself
  //   - A selected leaf → the subnet the leaf lives in (via
  //     `subnetOfShape`)
  // Any aggregated edge whose src or tgt subnet is in this set gets
  // auto-expanded, revealing the constituent leaf edges. Merges with
  // `expandedEdgeSet` so explicit clicks still apply.
  const selectedSubnetIds = new Set<string>();
  for (const selectedId of options.selectedIds ?? []) {
    if (selectedId.startsWith('frame:')) {
      const containerId = selectedId.slice('frame:'.length);
      const f = layout.frames.get(containerId);
      if (f?.resourceType === 'subnet') selectedSubnetIds.add(containerId);
      continue;
    }
    const ownerSubnet = subnetOfShape.get(selectedId);
    if (ownerSubnet) selectedSubnetIds.add(ownerSubnet);
  }
  // First pass: group by pair so we can decide which pairs are
  // suppressed (expanded) per `expandedEdgeSet`.
  const pairMembers = new Map<string, Array<typeof rewrittenFlow[number]>>();
  for (const e of rewrittenFlow) {
    const srcSubnet = subnetOfShape.get(e.source);
    const tgtSubnet = subnetOfShape.get(e.target);
    if (!collapseEdges || !srcSubnet || !tgtSubnet || srcSubnet === tgtSubnet) {
      intraSubnetFlow.push(e);
      continue;
    }
    const pairKey = `${srcSubnet}->${tgtSubnet}`;
    const arr = pairMembers.get(pairKey) ?? [];
    arr.push(e);
    pairMembers.set(pairKey, arr);
  }
  // Track which leaf edges were emitted via an expanded aggregate so
  // we can attach `expandedFrom` in attrs — lets the UI round-trip
  // back to collapsed (re-click to re-aggregate).
  const expandedFromByEdgeId = new Map<string, string>();

  // Second pass: materialise each pair as either an aggregate or an
  // expanded set of leaf-level edges. Expansion fires from either
  // the explicit `expandedEdgeSet` (click) OR the selection-derived
  // subnet set (frame or leaf selection touches one of the pair's
  // endpoints).
  for (const [pairKey, members] of pairMembers) {
    const aggId = `agg:${pairKey}`;
    const first = members[0];
    const srcSubnet = subnetOfShape.get(first.source)!;
    const tgtSubnet = subnetOfShape.get(first.target)!;
    const autoExpanded =
      selectedSubnetIds.has(srcSubnet) || selectedSubnetIds.has(tgtSubnet);
    if (expandedEdgeSet.has(aggId) || autoExpanded) {
      for (const m of members) {
        intraSubnetFlow.push(m);
        expandedFromByEdgeId.set(m.id, aggId);
      }
      continue;
    }
    const kinds = new Set<string>();
    for (const m of members) kinds.add(m.kind);
    aggregatedByPair.set(pairKey, {
      id: aggId,
      srcSubnet,
      tgtSubnet,
      memberIds: members.map((m) => m.id),
      kinds,
    });
  }

  const patterns: MatchedPattern[] = patternRecognizer.recognize({
    layout,
    manifestByResourceId,
    flowEdges: rewrittenFlow.map((e) => ({
      source: e.source,
      target: e.target,
      kind: e.kind,
    })),
  });
  const patternByMemberId = new Map<string, MatchedPattern>();
  for (const p of patterns) {
    for (const id of p.memberIds) {
      if (!patternByMemberId.has(id)) patternByMemberId.set(id, p);
    }
  }

  const sharedRefsByReferrer = new Map<
    string,
    Array<{ resourceId: string; label: string; manifestId: string }>
  >();
  for (const ref of layout.sharedReferences.values()) {
    for (const referrer of ref.referencedBy) {
      const arr = sharedRefsByReferrer.get(referrer) ?? [];
      arr.push({
        resourceId: ref.resourceId,
        label: ref.label,
        manifestId: ref.manifestId,
      });
      sharedRefsByReferrer.set(referrer, arr);
    }
  }

  // ── Build Scene nodes ────────────────────────────────────────
  const sceneNodes: Node[] = [];

  for (const leaf of slice.leafNodes) {
    if (layout.hiddenMemberIds.has(leaf.id)) continue;
    const pos = layout.positions.get(leaf.id);
    if (!pos) continue;
    const manifestId = manifestIdOf(leaf);
    const cluster = layout.clusters.get(leaf.id);
    const width = cluster ? cluster.width : defaultSize.width;
    const height = cluster ? cluster.height : defaultSize.height;
    const props = (leaf.props ?? {}) as Record<string, unknown>;
    const pattern = patternByMemberId.get(leaf.id);
    const sharedRefs = sharedRefsByReferrer.get(leaf.id);

    sceneNodes.push({
      id: leaf.id,
      type: manifestId,
      x: pos.x + ORIGIN.x,
      y: pos.y + ORIGIN.y,
      w: width,
      h: height,
      name: String(leaf.label ?? manifestId),
      attrs: {
        ...props,
        ...(costAttribution.monthlySpendById.has(leaf.id)
          ? { monthlySpend: costAttribution.monthlySpendById.get(leaf.id) }
          : {}),
        ...(findingAttribution.severityById.has(leaf.id)
          ? {
              findingSeverity: findingAttribution.severityById.get(leaf.id),
              findingCount: findingAttribution.countById.get(leaf.id) ?? 1,
            }
          : {}),
        ...(activeChipIds.size > 0 && !nodePassesActiveChips(leaf, activeChipIds, chipEvalCtx)
          ? { filterDimmed: true, dimmed: true }
          : {}),
        resourceId: leaf.id,
        name: String(leaf.label ?? manifestId),
        resourceType: resourceTypeOf(manifestId),
        importedFrom: 'compass-explorer',
        ...(cluster
          ? {
              cluster: true,
              clusterCount: cluster.count,
              clusterMemberIds: cluster.memberIds,
              clusterLabel: cluster.label,
              stack: {
                count: cluster.count,
                memberIds: cluster.memberIds,
                roleLabel: cluster.roleLabel ?? undefined,
                resourceType: cluster.resourceType,
              },
            }
          : {}),
        ...(sharedRefs ? { sharedRefs } : {}),
        ...(pattern
          ? {
              pattern: {
                patternId: pattern.patternId,
                label: pattern.label,
                memberIds: pattern.memberIds,
                isAnchor: pattern.anchorId === leaf.id,
                hints: pattern.hints,
              },
            }
          : {}),
      } as Record<string, unknown>,
    } as Node);
  }

  // Containers become "frame" nodes. The existing renderer handles
  // frames as a separate layer, so we attach their bounds to shape
  // nodes of type 'frame'. Since the Scene model doesn't distinguish
  // frames vs shapes cleanly at this call site, we emit them as nodes
  // with `type: 'frame'` — downstream Yjs integrations would use a
  // separate frames map, but the Explorer is read-only and renders
  // frames via InfraFrame metadata in attrs.
  // Palette for container chrome. Accent colours match the existing
  // bubble card kind-accent palette so L2 → L3 transitions read as
  // continuous visual context. Subnet-tier frames (public / private /
  // data) tint off the subnet colour; tier-group frames render in a
  // neutral slate so they don't fight the enclosing subnet tier.
  const containerAccent = (
    resourceType: string,
    tier: string | undefined,
  ): { stroke: string; fill: string } => {
    if (resourceType === 'tier-group') {
      // Mid-slate stroke so the "PUBLIC subnets" / "PRIVATE subnets"
      // title reads clearly against the page background without fighting
      // the enclosed subnet colour. Lighter tints looked washed out.
      return { stroke: '#64748b', fill: 'rgba(148,163,184,0.06)' };
    }
    if (resourceType === 'vpc') {
      return { stroke: '#22c55e', fill: 'rgba(34,197,94,0.04)' };
    }
    if (resourceType === 'subnet') {
      if (tier === 'public') return { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.04)' };
      if (tier === 'private') return { stroke: '#3b82f6', fill: 'rgba(59,130,246,0.04)' };
      if (tier === 'data') return { stroke: '#10b981', fill: 'rgba(16,185,129,0.04)' };
      return { stroke: '#94a3b8', fill: 'rgba(148,163,184,0.04)' };
    }
    if (resourceType === 'account') {
      return { stroke: '#0ea5e9', fill: 'rgba(14,165,233,0.04)' };
    }
    return { stroke: '#94a3b8', fill: 'rgba(148,163,184,0.04)' };
  };

  for (const frame of layout.frames.values()) {
    const isTierGroup = frame.containerId.startsWith('tier-group:');
    const containerNode = slice.containerNodes.find((c) => c.id === frame.containerId);
    const rawLabel = String(containerNode?.label ?? frame.resourceType);
    const tier = frame.tier;
    // Human-readable title. Tier-group frames are synthesised so don't
    // have a real node label — compose one from the tier name.
    const title = isTierGroup
      ? `${(tier ?? 'subnet').toUpperCase()} subnets`
      : rawLabel;
    const { stroke, fill } = containerAccent(frame.resourceType, tier);
    const frameKey = `frame:${frame.containerId}`;
    const isExpanded = expandedSet.has(frameKey) || expandedSet.has(frame.containerId);
    const isDimmed = expandedSet.size > 0 && !isExpanded;
    // Z-order: container frames paint back-to-front in containment
    // order — outermost first, innermost last. `NodesRenderer` sorts
    // ascending by `zIndex`, so lower = painted earlier = further back.
    // The containment hierarchy here is:
    //   vpc → tier-group → subnet → leaves
    // So tier-group sits BEHIND the subnets it wraps (title bar reads
    // "PUBLIC subnets" from behind the subnet frames) and IN FRONT of
    // the VPC. Leaves stay at default zIndex=0 so they always paint
    // on top of every container frame.
    const frameZ =
      frame.resourceType === 'vpc'
        ? -30
        : isTierGroup
          ? -25
          : frame.resourceType === 'subnet'
            ? -20
            : -10;
    sceneNodes.push({
      id: frameKey,
      type: 'frame',
      zIndex: frameZ,
      x: frame.x + ORIGIN.x,
      y: frame.y + ORIGIN.y,
      // Emit both the modern `width`/`height` and deprecated `w`/`h` so
      // every consumer in the canvas pipeline (FrameNode, overlays,
      // hit-testing) picks them up regardless of which field name they
      // look at.
      w: frame.width,
      h: frame.height,
      width: frame.width,
      height: frame.height,
      name: title,
      strokeColor: stroke,
      fillColor: fill,
      strokeWidth: 1.5,
      attrs: {
        frameType: 'basic',
        title,
        resourceType: frame.resourceType,
        containerId: frame.containerId,
        subnetTier: tier,
        mirrored: frame.mirrored ?? false,
        tierGroup: isTierGroup,
        importedFrom: 'compass-explorer-frame',
        expanded: isExpanded,
        dimmed: isDimmed,
      } as Record<string, unknown>,
    } as Node);
  }

  // ── Build Scene edges ────────────────────────────────────────
  // Placement comes from `InfraLayoutEngine` (deterministic, runtime
  // agnostic so it also works inside the Cloudflare Workers
  // materializer). Edge routing is handled by a client-side ELK pass
  // below — nodes stay pinned, ELK just computes obstacle-avoiding
  // orthogonal polylines. Hand anchor-picking is only used as a
  // fallback if ELK bails.
  type Box = { x: number; y: number; w: number; h: number };
  const boxByNodeId = new Map<string, Box>();
  for (const node of sceneNodes) {
    const w = (node as { w?: number }).w ?? 0;
    const h = (node as { h?: number }).h ?? 0;
    boxByNodeId.set(node.id, { x: node.x, y: node.y, w, h });
  }

  const pickFallbackAnchors = (
    fromBox: Box | undefined,
    toBox: Box | undefined,
  ): { fromAnchor: 'top'|'right'|'bottom'|'left'; toAnchor: 'top'|'right'|'bottom'|'left' } => {
    if (!fromBox || !toBox) return { fromAnchor: 'right', toAnchor: 'left' };
    const fc = { x: fromBox.x + fromBox.w / 2, y: fromBox.y + fromBox.h / 2 };
    const tc = { x: toBox.x + toBox.w / 2, y: toBox.y + toBox.h / 2 };
    const dx = tc.x - fc.x;
    const dy = tc.y - fc.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      return dx >= 0
        ? { fromAnchor: 'right', toAnchor: 'left' }
        : { fromAnchor: 'left', toAnchor: 'right' };
    }
    return dy >= 0
      ? { fromAnchor: 'bottom', toAnchor: 'top' }
      : { fromAnchor: 'top', toAnchor: 'bottom' };
  };

  const sceneEdges: Edge[] = [];
  const emittedEdgeKey = new Set<string>();

  // Pass 1: intra-subnet leaf-to-leaf edges (unchanged semantics).
  for (const e of intraSubnetFlow) {
    const srcManifest = manifestByResourceId.get(e.source) ?? '';
    const tgtManifest = manifestByResourceId.get(e.target) ?? '';
    const edgeTier = classifyEdgeTier(
      { kind: e.kind },
      classifyInfraTier(srcManifest),
      classifyInfraTier(tgtManifest),
    );
    if (edgeTier === 'structural' || edgeTier === 'default') continue;
    if (e.source === e.target) continue;
    const key = `${e.kind}:${e.source}->${e.target}`;
    if (emittedEdgeKey.has(key)) continue;
    emittedEdgeKey.add(key);

    const { fromAnchor, toAnchor } = pickFallbackAnchors(
      boxByNodeId.get(e.source),
      boxByNodeId.get(e.target),
    );

    const fromAgg = expandedFromByEdgeId.get(e.id);
    sceneEdges.push({
      id: e.id,
      type: e.kind,
      from: e.source,
      to: e.target,
      fromAnchor,
      toAnchor,
      attrs: {
        edgeTier,
        infraEdgeKind: e.kind,
        routingType: 'orthogonal',
        // When this leaf edge was revealed by expanding an aggregate,
        // stamp the aggregate id so clicking it can re-collapse.
        ...(fromAgg ? { expandedFrom: fromAgg } : {}),
      } as Record<string, unknown>,
    } as Edge);
  }

  // Pass 2: aggregated subnet-to-subnet edges. Endpoints are frame
  // node ids (`frame:<containerId>`) so the renderer + router both
  // treat them as anchored to the frame rect. Stroke width scales
  // with member count so thicker aggregates signal heavier traffic
  // between those subnets.
  const aggregateStrokeWidth = (count: number): number => {
    if (count <= 1) return 2;
    if (count <= 3) return 3;
    if (count <= 6) return 4;
    if (count <= 12) return 5;
    return 6;
  };
  for (const agg of aggregatedByPair.values()) {
    const fromFrameId = `frame:${agg.srcSubnet}`;
    const toFrameId = `frame:${agg.tgtSubnet}`;
    const { fromAnchor, toAnchor } = pickFallbackAnchors(
      boxByNodeId.get(fromFrameId),
      boxByNodeId.get(toFrameId),
    );
    sceneEdges.push({
      id: agg.id,
      // 'aggregated' is a compass-explorer-only marker. Cast through
      // unknown so TS's strict EdgeType union doesn't complain —
      // renderers branch on `attrs.aggregated`, not on type.
      type: 'aggregated' as unknown as Edge['type'],
      from: fromFrameId,
      to: toFrameId,
      fromAnchor,
      toAnchor,
      attrs: {
        edgeTier: 'data',
        infraEdgeKind: 'aggregated',
        routingType: 'orthogonal',
        aggregated: true,
        aggregateCount: agg.memberIds.length,
        memberEdgeIds: agg.memberIds,
        memberKinds: [...agg.kinds],
        width: aggregateStrokeWidth(agg.memberIds.length),
      } as Record<string, unknown>,
    } as Edge);
  }

  // ── Pass edges through the orthogonal router ─────────────────
  // Domain-agnostic A*-on-Hanan-grid pathfinder in `core/routing`.
  // Gives us obstacle-aware orthogonal routes with turn penalty so
  // paths don't zig-zag. Frames pass as NON-blocking obstacles (an
  // edge is allowed to cross a frame's border — its children are
  // the real obstacles). Leaf cards are blocking.
  //
  // On success each edge gets `layoutPoints` + `layoutSource:
  // 'compass-explorer'` so EdgeConnector renders the polyline
  // verbatim. On failure (no path / timeout) we fall through to
  // EdgeConnector's default router using the anchors stamped above.
  try {
    const obstacles = [] as Array<{
      id: string;
      rect: { x: number; y: number; width: number; height: number };
      blocking: boolean;
      segmentOnly?: boolean;
    }>;
    for (const node of sceneNodes) {
      const w = (node as { w?: number }).w ?? 0;
      const h = (node as { h?: number }).h ?? 0;
      if (!w || !h) continue;
      const isFrame = node.type === 'frame';
      obstacles.push({
        id: node.id,
        rect: { x: node.x, y: node.y, width: w, height: h },
        // Frames ARE blockers at the segment level — otherwise
        // cross-subnet edges cut straight through sibling subnets.
        // But they're `segmentOnly`, so leaf ports inside a frame
        // can still start/end routes. The router auto-adds any frame
        // CONTAINING an endpoint to that edge's ignoreRects (via the
        // `containingRects` pass), so intra-subnet edges exit their
        // enclosing frame cleanly while cross-subnet edges route
        // AROUND unrelated frames.
        blocking: true,
        segmentOnly: isFrame,
      });
    }
    const routeRequests = sceneEdges.map((e) => ({
      id: e.id,
      fromObstacleId: e.from,
      toObstacleId: e.to,
    }));
    const routes = routeOrthogonalEdges(obstacles, routeRequests, {
      // Bigger clearance = more breathing room between edges and
      // obstacle borders. 14 was tight enough that routes visually
      // hugged every card. 24 keeps a clear gutter.
      clearance: 24,
      turnPenalty: 40,
      // Drop failed edges entirely rather than falling back to a
      // straight line that crosses through unrelated shapes. Better
      // to hide a connection we couldn't route cleanly than to draw
      // it through the middle of three other cards. Router surfaces
      // the count via a console warn so we can diagnose layout bugs
      // (e.g. overlapping obstacles from ELK).
      dropOnFailure: true,
      laneSpacing: 16,
      debug: isCompassRouterDebugEnabled(),
    });
    // Two-pass: stamp layoutPoints onto edges that ROUTED cleanly
    // (≥4 points from the A* search); REMOVE edges whose route
    // dropped so EdgeConnector's default router doesn't silently
    // draw them as obstacle-crossing fallbacks.
    const droppedEdgeIds = new Set<string>();
    for (const edge of sceneEdges) {
      const routed = routes.get(edge.id);
      if (!routed || routed.points.length < 4) {
        droppedEdgeIds.add(edge.id);
        continue;
      }
      (edge as Edge).fromAnchor = routed.fromSide;
      (edge as Edge).toAnchor = routed.toSide;
      (edge.attrs as Record<string, unknown>).layoutPoints = routed.points;
      (edge.attrs as Record<string, unknown>).layoutSource = 'compass-explorer';
    }
    if (droppedEdgeIds.size > 0) {
      for (let i = sceneEdges.length - 1; i >= 0; i--) {
        if (droppedEdgeIds.has(sceneEdges[i].id)) sceneEdges.splice(i, 1);
      }
    }
    const totalMembers = [...aggregatedByPair.values()].reduce(
      (s, a) => s + a.memberIds.length,
      0,
    );
    if (isCompassRouterDebugEnabled()) {
      console.info('[compass:router]', {
        obstacles: obstacles.length,
        edges: routeRequests.length,
        routed: routes.size,
        dropped: droppedEdgeIds.size,
        intraSubnetEdges: intraSubnetFlow.length,
        aggregatedEdges: aggregatedByPair.size,
        collapsedMembers: totalMembers,
        collapseRatio:
          aggregatedByPair.size > 0
            ? (totalMembers / aggregatedByPair.size).toFixed(1)
            : '—',
      });
    }
  } catch (err) {
    // Always log router exceptions — silent try/catch made it
    // impossible to distinguish "no failures" from "router exploded".
    console.error('[compass:router] FAILED', err);
  }

  return {
    version: 1,
    nodes: sceneNodes,
    edges: sceneEdges,
    meta: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

function buildBubbleScene(
  bubbles: BubbleNode[],
  expandedSet: Set<string>,
  graph: PersistedGraph,
  expandedContainerRefs: ReadonlySet<string>,
  memberIdsByCluster: ReadonlyMap<string, readonly string[]> = new Map(),
): Scene {
  // Shelf-packing flow so expanded bubbles (which grow to reveal their
  // interior dot grid) can coexist with compact siblings in the same
  // grid. Each bubble emits its own width/height; rows wrap when the
  // running width would exceed GRID_W.
  const GRID_W = 2000; // allow wide scenes so big cards sit next to tiny ones
  const anyExpanded = expandedSet.size > 0;

  // Cross-bubble topology — peering / TGW / cross-VPC SG references /
  // IAM trust show up here as aggregated edges between bubble cards.
  // Boundary chips land on expanded bubbles whose peers are still
  // collapsed so users can jump straight to the connected neighbour.
  const topology = computeBubbleTopology(graph, bubbles, expandedContainerRefs);

  // Findings — "talking points" layer that turns the inventory view
  // into an advisory view. Attached per-bubble as attrs.findings so
  // the renderer can badge cards; also aggregated globally for the
  // CompassFindingsStrip overlay.
  const findingsReport = FindingsRegistry.run({ bubbles, graph });

  // Sort by resource count descending within each env group so the
  // biggest VPC in each environment anchors its row. Env grouping
  // then lays these out in labelled bands stacked top-to-bottom so
  // the canvas reads like a hierarchical inventory: prod first,
  // then stage, dev, test, other.
  const sorted = [...bubbles].sort((a, b) => b.resourceCount - a.resourceCount);
  const envGroups = groupBubblesByEnv(sorted);

  // Flatten the env-sorted bubbles back into a processing list while
  // recording which env group each bubble belongs to — the placement
  // loop uses this to start a new row whenever the env key flips.
  const envByBubbleId = new Map<string, string>();
  const envMetaByKey = new Map<string, { label: string; accent: string }>();
  const sortedBubbles: BubbleNode[] = [];
  for (const group of envGroups) {
    envMetaByKey.set(group.key, { label: group.label, accent: group.accent });
    for (const b of group.bubbles) {
      envByBubbleId.set(b.id, group.key);
      sortedBubbles.push(b);
    }
  }

  const sized = sortedBubbles.map((b) => {
    const isExpanded = expandedSet.has(b.id) || expandedSet.has(b.referenceId);
    const hasFindings = (findingsReport.byBubble.get(b.id)?.length ?? 0) > 0;
    const { width, height } = bubbleDimensions(b, isExpanded, hasFindings);
    return { bubble: b, isExpanded, width, height };
  });

  // Shelf-pack within each env group; start a new row when the env
  // key changes. Each env section leads with a label placement so the
  // scene builder can emit a `compass_env_label` node there.
  const ENV_LABEL_H = 28;
  const ENV_GROUP_GAP = 24;

  const placements: Array<{ x: number; y: number; w: number; h: number; index: number }> = [];
  const envLabelPlacements: Array<{ envKey: string; y: number }> = [];
  let shelfY = 0;
  let shelfX = 0;
  let shelfH = 0;
  let currentEnv: string | null = null;

  sized.forEach((item, i) => {
    const env = envByBubbleId.get(item.bubble.id) ?? 'other';
    const envChanged = env !== currentEnv;

    if (envChanged) {
      if (currentEnv !== null) {
        // Close previous shelf row for the prior env.
        shelfY += shelfH + ENV_GROUP_GAP;
      }
      envLabelPlacements.push({ envKey: env, y: shelfY });
      shelfY += ENV_LABEL_H;
      shelfX = 0;
      shelfH = 0;
      currentEnv = env;
    } else if (shelfX + item.width > GRID_W && shelfX > 0) {
      shelfY += shelfH + BUBBLE_GAP;
      shelfX = 0;
      shelfH = 0;
    }

    placements.push({ x: shelfX, y: shelfY, w: item.width, h: item.height, index: i });
    shelfX += item.width + BUBBLE_GAP;
    shelfH = Math.max(shelfH, item.height);
  });

  const nodes: Node[] = placements.map(({ x, y, w, h, index }) => {
    const { bubble: b, isExpanded } = sized[index];
    const isDimmed = anyExpanded && !isExpanded;
    const chips = topology.chipsByBubble.get(b.id);
    const findings = findingsReport.byBubble.get(b.id);
    const envAccent = envMetaByKey.get(envByBubbleId.get(b.id) ?? 'other')?.accent;
    return {
      id: b.id,
      type: 'compass_bubble',
      x: ORIGIN.x + x,
      y: ORIGIN.y + y,
      w,
      h,
      name: b.label,
      attrs: {
        label: b.label,
        kind: b.kind,
        referenceId: b.referenceId,
        bubbleId: b.id,
        resourceCount: b.resourceCount,
        tierBreakdown: b.tierBreakdown,
        ...(envAccent ? { sectionAccent: envAccent } : {}),
        expanded: isExpanded,
        dimmed: isDimmed,
        ...(b.interior ? { interior: b.interior } : {}),
        ...(b.memberIds && b.memberIds.length > 0 ? { memberIds: b.memberIds } : {}),
        ...(b.groupKind ? { groupKind: b.groupKind } : {}),
        ...(chips && chips.length > 0 ? { boundaryChips: chips } : {}),
        ...(findings && findings.length > 0 ? { findings } : {}),
        ...(memberIdsByCluster.has(b.id)
          ? { cluster: true, memberIds: memberIdsByCluster.get(b.id) }
          : {}),
        ...(b.monthlyCostUsd !== undefined
          ? { monthlyCostUsd: b.monthlyCostUsd, costCoverage: b.costCoverage }
          : {}),
        ...(b.findingSeverity
          ? { findingSeverity: b.findingSeverity, findingCount: b.findingCount ?? 1 }
          : {}),
        ...(b.filterDimmed ? { filterDimmed: true, dimmed: true } : {}),
        ...(b.utilizationPct !== undefined ? { utilizationPct: b.utilizationPct } : {}),
        ...(b.wastedMonthlyUsd !== undefined ? { wastedMonthlyUsd: b.wastedMonthlyUsd } : {}),
        ...(b.scope ? { scope: b.scope } : {}),
      } as Record<string, unknown>,
    } as Node;
  });

  // Env section labels — one per group, positioned at the top-left of
  // each group's shelf row so the canvas reads like a labelled
  // hierarchy (PROD / STAGE / DEV / OTHER).
  for (const { envKey, y } of envLabelPlacements) {
    const meta = envMetaByKey.get(envKey);
    if (!meta) continue;
    const groupBubbleCount = envGroups.find((g) => g.key === envKey)?.bubbles.length ?? 0;
    nodes.push({
      id: `env-label:${envKey}`,
      type: 'compass_env_label',
      x: ORIGIN.x,
      y: ORIGIN.y + y,
      w: 400,
      h: ENV_LABEL_H,
      name: meta.label,
      attrs: {
        text: meta.label,
        accent: meta.accent,
        count: groupBubbleCount,
      } as Record<string, unknown>,
    } as Node);
  }

  // Cross-bubble edges — thin lines per connected pair. Non-expanded
  // pairs dim to match sibling cards; pairs with any endpoint
  // expanded stay full-weight so the user's active context wins.
  const edges: Edge[] = topology.edges.map((e) => {
    const bothInactive = anyExpanded
      && !expandedSet.has(e.fromBubbleId)
      && !expandedSet.has(e.toBubbleId)
      && !expandedContainerRefs.has(e.fromBubbleId.replace(/^bubble:/, ''))
      && !expandedContainerRefs.has(e.toBubbleId.replace(/^bubble:/, ''));
    return {
      id: e.id,
      type: 'line',
      from: e.fromBubbleId,
      to: e.toBubbleId,
      attrs: {
        bubbleEdge: true,
        count: e.count,
        kinds: e.kinds,
        bothExpanded: e.bothExpanded,
        routingType: 'orthogonal',
        color: e.bothExpanded ? BUBBLE_EDGE_ACTIVE_COLOR : BUBBLE_EDGE_MUTED_COLOR,
        width: e.bothExpanded ? 2.25 : 1.5,
        endArrow: BUBBLE_EDGE_ARROW,
        dashed: false,
        dimmed: bothInactive,
        label: e.count > 1 ? `×${e.count}` : undefined,
      } as Record<string, unknown>,
    } as Edge;
  });
  routeCompassExplorerEdges(nodes, edges);

  return {
    version: 1,
    nodes,
    edges,
    meta: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

function buildFocusedVpcBubbleScene(
  bubbles: BubbleNode[],
  vpcId: string,
  graph: PersistedGraph,
  expandedSet: Set<string>,
  expandedContainerRefs: ReadonlySet<string>,
): Scene {
  const vpcNode = graph.nodes.find((node) => node.id === vpcId);
  const vpcTitle = String(vpcNode?.label ?? 'VPC');
  const anyExpanded = expandedSet.size > 0;
  const findingsReport = FindingsRegistry.run({ bubbles, graph });
  const topology = computeBubbleTopology(graph, bubbles, expandedContainerRefs);

  const isSupportBubble = (bubble: BubbleNode): boolean => {
    if (bubble.kind === 'subnet') return false;
    if (bubble.kind === 'resource_type') return true;
    if (bubble.groupKind !== 'resource_type') return false;

    const supportTypes = new Set([
      'alb',
      'elb',
      'igw',
      'iam',
      'iam_role',
      'nat',
      'nlb',
      'route_table',
      'sg',
      'security_group',
      'tgw',
      'vpc_endpoint',
    ]);
    const resourceTypes = Object.keys(bubble.tierBreakdown);
    return resourceTypes.length > 0 && resourceTypes.every((type) => supportTypes.has(type));
  };

  const support = bubbles.filter(isSupportBubble);
  const primary = bubbles.filter((bubble) => !isSupportBubble(bubble));
  const sections = [
    ...(support.length > 0
      ? [{ key: 'support', label: 'VPC-level services', accent: '#64748b', bubbles: support }]
      : []),
    ...(primary.length > 0
      ? [{
          key: 'primary',
          label: primary.some((bubble) => bubble.kind === 'subnet') ? 'Subnets' : 'Workloads',
          accent: '#22c55e',
          bubbles: primary,
        }]
      : []),
  ];

  const FRAME_X = ORIGIN.x;
  const FRAME_Y = ORIGIN.y;
  const FRAME_PAD_X = 32;
  const FRAME_PAD_TOP = 54;
  const FRAME_PAD_BOTTOM = 32;
  const SECTION_LABEL_H = 28;
  const topologyFanByBubbleId = new Map<string, number>();
  for (const edge of topology.edges) {
    topologyFanByBubbleId.set(edge.fromBubbleId, (topologyFanByBubbleId.get(edge.fromBubbleId) ?? 0) + 1);
    topologyFanByBubbleId.set(edge.toBubbleId, (topologyFanByBubbleId.get(edge.toBubbleId) ?? 0) + 1);
  }
  const maxTopologyFan = Math.max(0, ...topologyFanByBubbleId.values());
  const connectorPressure = Math.min(180, topology.edges.length * 7 + maxTopologyFan * 14);
  const SECTION_GAP = 56 + Math.min(96, connectorPressure * 0.5);
  const CARD_GAP = 48 + Math.min(56, connectorPressure * 0.28);
  const CONTENT_W = 1520 + Math.min(260, connectorPressure);

  type PlacedBubble = {
    bubble: BubbleNode;
    isExpanded: boolean;
    sectionAccent: string;
    x: number;
    y: number;
    w: number;
    h: number;
  };

  const placements: PlacedBubble[] = [];
  const sectionLabels: Array<{ key: string; label: string; accent: string; y: number; count: number }> = [];
  let cursorY = FRAME_PAD_TOP;
  let maxRight = FRAME_PAD_X + CONTENT_W;

  for (const section of sections) {
    sectionLabels.push({
      key: section.key,
      label: section.label,
      accent: section.accent,
      y: cursorY,
      count: section.bubbles.length,
    });
    cursorY += SECTION_LABEL_H;

    let rowX = FRAME_PAD_X;
    let rowH = 0;
    const sorted = [...section.bubbles].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
      if (b.resourceCount !== a.resourceCount) return b.resourceCount - a.resourceCount;
      return a.label.localeCompare(b.label);
    });

    for (const bubble of sorted) {
      const isExpanded = expandedSet.has(bubble.id) || expandedSet.has(bubble.referenceId);
      const hasFindings = (findingsReport.byBubble.get(bubble.id)?.length ?? 0) > 0;
      const { width, height } = bubbleDimensions(bubble, isExpanded, hasFindings);

      if (rowX > FRAME_PAD_X && rowX + width > FRAME_PAD_X + CONTENT_W) {
        cursorY += rowH + CARD_GAP;
        rowX = FRAME_PAD_X;
        rowH = 0;
      }

      placements.push({
        bubble,
        isExpanded,
        sectionAccent: section.accent,
        x: rowX,
        y: cursorY,
        w: width,
        h: height,
      });
      rowX += width + CARD_GAP;
      rowH = Math.max(rowH, height);
      maxRight = Math.max(maxRight, rowX - CARD_GAP);
    }

    cursorY += rowH + SECTION_GAP;
  }

  const frameWidth = Math.max(720, maxRight + FRAME_PAD_X);
  const frameHeight = Math.max(360, cursorY - SECTION_GAP + FRAME_PAD_BOTTOM);
  const nodes: Node[] = [
    {
      id: `frame:${vpcId}`,
      type: 'frame',
      zIndex: -30,
      x: FRAME_X,
      y: FRAME_Y,
      w: frameWidth,
      h: frameHeight,
      width: frameWidth,
      height: frameHeight,
      name: vpcTitle,
      strokeColor: '#22c55e',
      fillColor: 'rgba(34,197,94,0.04)',
      strokeWidth: 1.5,
      attrs: {
        frameType: 'basic',
        title: vpcTitle,
        resourceType: 'vpc',
        containerId: vpcId,
        importedFrom: 'compass-explorer-frame',
        depthProjection: true,
        expanded: expandedSet.has(`frame:${vpcId}`) || expandedSet.has(vpcId),
        dimmed: false,
      } as Record<string, unknown>,
    } as Node,
  ];

  for (const section of sectionLabels) {
    nodes.push({
      id: `depth-label:${vpcId}:${section.key}`,
      type: 'compass_env_label',
      x: FRAME_X + FRAME_PAD_X,
      y: FRAME_Y + section.y,
      w: 400,
      h: SECTION_LABEL_H,
      name: section.label,
      attrs: {
        text: section.label,
        accent: section.accent,
        count: section.count,
        importedFrom: 'compass-explorer-depth-label',
      } as Record<string, unknown>,
    } as Node);
  }

  for (const placed of placements) {
    const { bubble, isExpanded } = placed;
    const isDimmed = anyExpanded && !isExpanded;
    const chips = topology.chipsByBubble.get(bubble.id);
    const findings = findingsReport.byBubble.get(bubble.id);
    nodes.push({
      id: bubble.id,
      type: 'compass_bubble',
      x: FRAME_X + placed.x,
      y: FRAME_Y + placed.y,
      w: placed.w,
      h: placed.h,
      name: bubble.label,
      attrs: {
        label: bubble.label,
        kind: bubble.kind,
        referenceId: bubble.referenceId,
        bubbleId: bubble.id,
        resourceCount: bubble.resourceCount,
        tierBreakdown: bubble.tierBreakdown,
        parentFrameId: `frame:${vpcId}`,
        anchorId: anchorIdForBubble(vpcId, bubble),
        sectionAccent: placed.sectionAccent,
        expanded: isExpanded,
        dimmed: isDimmed,
        ...(bubble.interior ? { interior: bubble.interior } : {}),
        ...(bubble.memberIds && bubble.memberIds.length > 0 ? { memberIds: bubble.memberIds } : {}),
        ...(bubble.groupKind ? { groupKind: bubble.groupKind } : {}),
        ...(chips && chips.length > 0 ? { boundaryChips: chips } : {}),
        ...(findings && findings.length > 0 ? { findings } : {}),
        ...(bubble.monthlyCostUsd !== undefined
          ? { monthlyCostUsd: bubble.monthlyCostUsd, costCoverage: bubble.costCoverage }
          : {}),
        ...(bubble.findingSeverity
          ? { findingSeverity: bubble.findingSeverity, findingCount: bubble.findingCount ?? 1 }
          : {}),
        ...(bubble.filterDimmed ? { filterDimmed: true, dimmed: true } : {}),
        ...(bubble.utilizationPct !== undefined ? { utilizationPct: bubble.utilizationPct } : {}),
        ...(bubble.wastedMonthlyUsd !== undefined ? { wastedMonthlyUsd: bubble.wastedMonthlyUsd } : {}),
        ...(bubble.scope ? { scope: bubble.scope } : {}),
      } as Record<string, unknown>,
    } as Node);
  }

  const edges: Edge[] = topology.edges.map((edge) => {
    const bothInactive = anyExpanded
      && !expandedSet.has(edge.fromBubbleId)
      && !expandedSet.has(edge.toBubbleId)
      && !expandedContainerRefs.has(edge.fromBubbleId.replace(/^bubble:/, ''))
      && !expandedContainerRefs.has(edge.toBubbleId.replace(/^bubble:/, ''));
    return {
      id: edge.id,
      type: 'line',
      from: edge.fromBubbleId,
      to: edge.toBubbleId,
      attrs: {
        bubbleEdge: true,
        count: edge.count,
        kinds: edge.kinds,
        bothExpanded: edge.bothExpanded,
        routingType: 'orthogonal',
        color: edge.bothExpanded ? BUBBLE_EDGE_ACTIVE_COLOR : BUBBLE_EDGE_COLOR,
        width: edge.bothExpanded ? 2.25 : 1.75,
        endArrow: BUBBLE_EDGE_ARROW,
        dashed: false,
        dimmed: bothInactive,
        label: edge.count > 1 ? `x${edge.count}` : undefined,
      } as Record<string, unknown>,
    } as Edge;
  });
  routeCompassExplorerEdges(nodes, edges);

  return {
    version: 1,
    nodes,
    edges,
    meta: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

function anchorIdForBubble(vpcId: string, bubble: BubbleNode): string {
  if (bubble.kind === 'subnet') return `subnet:${bubble.referenceId}`;
  if (bubble.kind === 'workload') {
    return `workload:${vpcId}:${bubble.groupKind ?? 'resource_type'}:${bubble.label}`;
  }
  if (bubble.kind === 'resource_type') return `workload:${vpcId}:resource_type:${bubble.label}`;
  return `${bubble.kind}:${bubble.referenceId}`;
}

function buildFocusedVpcResourceScene(
  slice: ReturnType<typeof sliceForFocus>,
  vpcId: string,
  graph: PersistedGraph,
  defaultSize: { width: number; height: number },
  options: BuildSceneOptions,
): Scene {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const parentOf = new Map<string, string>();
  const childrenOf = new Map<string, string[]>();
  for (const edge of slice.containmentEdges) {
    if (!parentOf.has(edge.to)) parentOf.set(edge.to, edge.from);
    const children = childrenOf.get(edge.from) ?? [];
    children.push(edge.to);
    childrenOf.set(edge.from, children);
  }

  const containerTypeOf = (id: string): string => {
    const node = byId.get(id);
    return node ? resourceTypeOf(manifestIdOf(node)) : '';
  };
  const findAncestor = (id: string, kind: string): string | null => {
    let cursor = parentOf.get(id);
    let hops = 0;
    while (cursor && hops++ < 16) {
      if (containerTypeOf(cursor) === kind) return cursor;
      cursor = parentOf.get(cursor);
    }
    return null;
  };

  const vpcNode = byId.get(vpcId);
  const vpcTitle = String(vpcNode?.label ?? 'VPC');
  const subnets = slice.containerNodes
    .filter((node) => resourceTypeOf(manifestIdOf(node)) === 'subnet')
    .sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)));
  const subnetIds = new Set(subnets.map((subnet) => subnet.id));
  const leafIds = new Set(slice.leafNodes.map((resource) => resource.id));
  const resourcesBySubnet = new Map<string, typeof slice.leafNodes>();
  const vpcLevelResources: typeof slice.leafNodes = [];

  for (const resource of slice.leafNodes) {
    const subnetId = findAncestor(resource.id, 'subnet');
    if (subnetId && subnetIds.has(subnetId)) {
      const resources = resourcesBySubnet.get(subnetId) ?? [];
      resources.push(resource);
      resourcesBySubnet.set(subnetId, resources);
    } else {
      vpcLevelResources.push(resource);
    }
  }

  for (const resources of resourcesBySubnet.values()) {
    resources.sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)));
  }
  vpcLevelResources.sort((a, b) => String(a.label ?? a.id).localeCompare(String(b.label ?? b.id)));

  const vpcLevelResourceIdsForLayout = new Set(vpcLevelResources.map((resource) => resource.id));
  const containerEndpointForLayout = (resourceId: string): string | null => {
    const subnetId = findAncestor(resourceId, 'subnet');
    if (subnetId && subnetIds.has(subnetId)) return `frame:${subnetId}`;
    return vpcLevelResourceIdsForLayout.has(resourceId)
      ? `frame:${vpcId}:services`
      : null;
  };
  const aggregatePairIdsForLayout = new Set<string>();
  const aggregateFanForLayout = new Map<string, number>();
  for (const edge of slice.flowEdges) {
    if (!leafIds.has(edge.from) || !leafIds.has(edge.to) || edge.from === edge.to) continue;
    const fromContainer = containerEndpointForLayout(edge.from);
    const toContainer = containerEndpointForLayout(edge.to);
    if (!fromContainer || !toContainer || fromContainer === toContainer) continue;
    const pairId = `${fromContainer}->${toContainer}`;
    if (aggregatePairIdsForLayout.has(pairId)) continue;
    aggregatePairIdsForLayout.add(pairId);
    aggregateFanForLayout.set(fromContainer, (aggregateFanForLayout.get(fromContainer) ?? 0) + 1);
    aggregateFanForLayout.set(toContainer, (aggregateFanForLayout.get(toContainer) ?? 0) + 1);
  }
  const maxAggregateFanForLayout = Math.max(0, ...aggregateFanForLayout.values());
  const connectorPressure = Math.min(
    120,
    aggregatePairIdsForLayout.size * 5 + maxAggregateFanForLayout * 10,
  );

  const FRAME_X = ORIGIN.x;
  const FRAME_Y = ORIGIN.y;
  const FRAME_PAD_X = 32;
  const FRAME_PAD_TOP = 54;
  const FRAME_PAD_BOTTOM = 32;
  const SUBNET_FRAME_W = 600;
  const SUBNET_COLUMN_GAP = 112 + connectorPressure;
  const CONTENT_W = SUBNET_FRAME_W * 2 + SUBNET_COLUMN_GAP;
  const SECTION_GAP = 36 + Math.min(72, connectorPressure * 0.55);
  const INNER_PAD = 20;
  const FRAME_TITLE_H = 36;
  const CARD_GAP = 18;
  const CONTAINER_ACCENTS = [
    '#2563eb',
    '#7c3aed',
    '#0891b2',
    '#16a34a',
    '#d97706',
    '#db2777',
    '#0f766e',
    '#4f46e5',
  ];
  const colorForContainer = (containerId: string): string => {
    if (containerId === `frame:${vpcId}`) return '#22c55e';
    if (containerId === `frame:${vpcId}:services`) return '#64748b';
    let hash = 0;
    for (let i = 0; i < containerId.length; i++) {
      hash = (hash * 31 + containerId.charCodeAt(i)) >>> 0;
    }
    return CONTAINER_ACCENTS[hash % CONTAINER_ACCENTS.length];
  };
  const fillForColor = (color: string): string => {
    const hex = color.replace('#', '');
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.07)`;
  };

  type ResourcePlacement = {
    resource: typeof slice.leafNodes[number];
    x: number;
    y: number;
    parentFrameId: string;
  };
  const resourcePlacements: ResourcePlacement[] = [];
  const frames: Node[] = [];

  const layoutResources = (
    resources: typeof slice.leafNodes,
    frameX: number,
    frameY: number,
    innerWidth: number,
    parentFrameId: string,
  ): number => {
    if (resources.length === 0) return 0;
    let x = frameX + INNER_PAD;
    let y = frameY + FRAME_TITLE_H;
    let rowH = 0;
    let maxBottom = y;
    for (const resource of resources) {
      if (x > frameX + INNER_PAD && x + defaultSize.width > frameX + innerWidth - INNER_PAD) {
        y += rowH + CARD_GAP;
        x = frameX + INNER_PAD;
        rowH = 0;
      }
      resourcePlacements.push({ resource, x, y, parentFrameId });
      x += defaultSize.width + CARD_GAP;
      rowH = Math.max(rowH, defaultSize.height);
      maxBottom = Math.max(maxBottom, y + defaultSize.height);
    }
    return maxBottom - frameY + INNER_PAD;
  };

  let cursorY = FRAME_PAD_TOP;
  let maxRight = FRAME_PAD_X + CONTENT_W;

  if (vpcLevelResources.length > 0) {
    const supportFrameId = `frame:${vpcId}:services`;
    const frameX = FRAME_PAD_X;
    const frameY = cursorY;
    const frameHeight = Math.max(
      132,
      layoutResources(vpcLevelResources, frameX, frameY, CONTENT_W, supportFrameId),
    );
    frames.push(makeCompassFrame({
      id: supportFrameId,
      x: FRAME_X + frameX,
      y: FRAME_Y + frameY,
      width: CONTENT_W,
      height: frameHeight,
      title: 'VPC-level services',
      resourceType: 'vpc-services',
      containerId: `${vpcId}:services`,
      stroke: colorForContainer(supportFrameId),
      fill: fillForColor(colorForContainer(supportFrameId)),
      zIndex: -25,
      extraAttrs: { parentFrameId: `frame:${vpcId}`, depthProjection: true },
    }));
    cursorY += frameHeight + SECTION_GAP;
  }

  let rowX = FRAME_PAD_X;
  let rowY = cursorY;
  let rowH = 0;
  for (const subnet of subnets) {
    const resources = resourcesBySubnet.get(subnet.id) ?? [];
    if (rowX > FRAME_PAD_X && rowX + SUBNET_FRAME_W > FRAME_PAD_X + CONTENT_W) {
      rowY += rowH + SECTION_GAP;
      rowX = FRAME_PAD_X;
      rowH = 0;
    }

    const frameId = `frame:${subnet.id}`;
    const frameAccent = colorForContainer(frameId);
    const frameHeight = Math.max(
      132,
      layoutResources(resources, rowX, rowY, SUBNET_FRAME_W, frameId),
    );
    frames.push(makeCompassFrame({
      id: frameId,
      x: FRAME_X + rowX,
      y: FRAME_Y + rowY,
      width: SUBNET_FRAME_W,
      height: frameHeight,
      title: String(subnet.label ?? subnet.id),
      resourceType: 'subnet',
      containerId: subnet.id,
      stroke: frameAccent,
      fill: fillForColor(frameAccent),
      zIndex: -20,
      extraAttrs: {
        parentFrameId: `frame:${vpcId}`,
        anchorId: `subnet:${subnet.id}`,
        depthProjection: true,
      },
    }));
    rowX += SUBNET_FRAME_W + SUBNET_COLUMN_GAP;
    rowH = Math.max(rowH, frameHeight);
    maxRight = Math.max(maxRight, rowX - SECTION_GAP);
  }
  if (subnets.length > 0) cursorY = rowY + rowH;

  const vpcFrameWidth = Math.max(720, maxRight + FRAME_PAD_X);
  const vpcFrameHeight = Math.max(360, cursorY + FRAME_PAD_BOTTOM);
  const sceneNodes: Node[] = [
    makeCompassFrame({
      id: `frame:${vpcId}`,
      x: FRAME_X,
      y: FRAME_Y,
      width: vpcFrameWidth,
      height: vpcFrameHeight,
      title: vpcTitle,
      resourceType: 'vpc',
      containerId: vpcId,
      stroke: '#22c55e',
      fill: 'rgba(34,197,94,0.04)',
      zIndex: -30,
      extraAttrs: { depthProjection: true, anchorId: `vpc:${vpcId}` },
    }),
    ...frames,
  ];

  for (const placement of resourcePlacements) {
    const manifestId = manifestIdOf(placement.resource);
    const props = (placement.resource.props ?? {}) as Record<string, unknown>;
    sceneNodes.push({
      id: placement.resource.id,
      type: manifestId,
      x: FRAME_X + placement.x,
      y: FRAME_Y + placement.y,
      w: defaultSize.width,
      h: defaultSize.height,
      name: String(placement.resource.label ?? manifestId),
      attrs: {
        ...props,
        ...(options.monthlySpendById?.has(placement.resource.id)
          ? { monthlySpend: options.monthlySpendById.get(placement.resource.id) }
          : {}),
        ...(options.findingSeverityById?.has(placement.resource.id)
          ? {
              findingSeverity: options.findingSeverityById.get(placement.resource.id),
              findingCount: options.findingCountById?.get(placement.resource.id) ?? 1,
            }
          : {}),
        ...(options.activeFilterChipIds && options.activeFilterChipIds.length > 0 &&
          !nodePassesActiveChips(
            placement.resource,
            new Set(options.activeFilterChipIds),
            { monthlySpendById: options.monthlySpendById ?? new Map() },
          )
          ? { filterDimmed: true, dimmed: true }
          : {}),
        resourceId: placement.resource.id,
        name: String(placement.resource.label ?? manifestId),
        resourceType: resourceTypeOf(manifestId),
        importedFrom: 'compass-explorer',
        parentFrameId: placement.parentFrameId,
        anchorId: `resource:${placement.resource.id}`,
        depthProjection: true,
      } as Record<string, unknown>,
    } as Node);
  }

  const visibleIds = new Set(sceneNodes.map((node) => node.id));
  const sceneEdges: Edge[] = [];
  const emitted = new Set<string>();
  const subnetOfResource = new Map<string, string>();
  const vpcLevelResourceIds = new Set(vpcLevelResources.map((resource) => resource.id));
  const containerEndpointForResource = (resourceId: string): string | null => {
    const subnetId = subnetOfResource.get(resourceId);
    if (subnetId) return `frame:${subnetId}`;
    return vpcLevelResourceIds.has(resourceId)
      ? `frame:${vpcId}:services`
      : null;
  };

  for (const resource of slice.leafNodes) {
    const subnetId = findAncestor(resource.id, 'subnet');
    if (subnetId && subnetIds.has(subnetId)) {
      subnetOfResource.set(resource.id, subnetId);
    }
  }

  const aggregateStrokeWidth = (count: number): number => {
    if (count <= 1) return 2;
    if (count <= 3) return 3;
    if (count <= 6) return 4;
    if (count <= 12) return 5;
    return 6;
  };
  type FocusedVpcFlowEdge = typeof slice.flowEdges[number];
  const aggregates = new Map<string, {
    id: string;
    from: string;
    to: string;
    members: FocusedVpcFlowEdge[];
    memberKinds: Set<string>;
  }>();
  const expandedEdgeSet = new Set(options.expandedEdgeIds ?? []);
  const selectedContainerIds = new Set<string>();
  const selectedLeafContainerIds = new Set<string>();
  const selectedLeafIds = new Set<string>();
  const relatedContainerIds = new Set<string>();
  const expandAllAggregates = (options.selectedIds ?? []).some((selectedId) =>
    selectedId === vpcId || selectedId === `frame:${vpcId}`
  );

  for (const selectedId of options.selectedIds ?? []) {
    if (selectedId === `frame:${vpcId}:services`) {
      selectedContainerIds.add(selectedId);
      continue;
    }
    if (selectedId.startsWith('frame:')) {
      const containerId = selectedId.slice('frame:'.length);
      if (subnetIds.has(containerId)) selectedContainerIds.add(selectedId);
      continue;
    }
    if (subnetIds.has(selectedId)) {
      selectedContainerIds.add(`frame:${selectedId}`);
      continue;
    }
    const endpointFrame = containerEndpointForResource(selectedId);
    if (endpointFrame) {
      selectedLeafIds.add(selectedId);
      selectedLeafContainerIds.add(endpointFrame);
    }
  }
  const hasConnectorSelection = expandAllAggregates || selectedContainerIds.size > 0 || selectedLeafIds.size > 0;
  const isEndpointRelatedToSelection = (
    fromId: string,
    toId: string,
    fromContainer: string | null,
    toContainer: string | null,
  ): boolean => {
    if (!hasConnectorSelection) return true;
    if (expandAllAggregates) return true;
    return (
      (fromContainer ? selectedContainerIds.has(fromContainer) : false) ||
      (toContainer ? selectedContainerIds.has(toContainer) : false) ||
      selectedLeafIds.has(fromId) ||
      selectedLeafIds.has(toId)
    );
  };
  const markRelatedContainers = (fromContainer: string | null, toContainer: string | null): void => {
    if (fromContainer) relatedContainerIds.add(fromContainer);
    if (toContainer) relatedContainerIds.add(toContainer);
  };

  const emitLeafEdge = (edge: FocusedVpcFlowEdge, expandedFrom?: string): void => {
    const fromContainer = containerEndpointForResource(edge.from);
    const toContainer = containerEndpointForResource(edge.to);
    const related = isEndpointRelatedToSelection(edge.from, edge.to, fromContainer, toContainer);
    const color = colorForContainer(fromContainer ?? toContainer ?? `frame:${vpcId}:services`);
    if (related) markRelatedContainers(fromContainer, toContainer);
    sceneEdges.push({
      id: edge.id,
      type: edge.type as Edge['type'],
      from: edge.from,
      to: edge.to,
      attrs: {
        edgeTier: 'data',
        infraEdgeKind: edge.type,
        routingType: 'orthogonal',
        color,
        opacity: related ? 0.96 : 0.14,
        width: related ? 2.75 : 1.25,
        sourceContainerId: fromContainer,
        targetContainerId: toContainer,
        ...(expandedFrom ? { expandedFrom } : {}),
      } as Record<string, unknown>,
    } as Edge);
  };

  for (const edge of slice.flowEdges) {
    if (!visibleIds.has(edge.from) || !visibleIds.has(edge.to)) continue;
    if (edge.from === edge.to) continue;
    const key = `${edge.type}:${edge.from}->${edge.to}`;
    if (emitted.has(key)) continue;
    emitted.add(key);

    const fromContainer = containerEndpointForResource(edge.from);
    const toContainer = containerEndpointForResource(edge.to);
    if (fromContainer && toContainer && fromContainer !== toContainer) {
      const aggregateKey = `${fromContainer}->${toContainer}`;
      let aggregate = aggregates.get(aggregateKey);
      if (!aggregate) {
        aggregate = {
          id: `agg:${fromContainer}->${toContainer}`,
          from: fromContainer,
          to: toContainer,
          members: [],
          memberKinds: new Set<string>(),
        };
        aggregates.set(aggregateKey, aggregate);
      }
      aggregate.members.push(edge);
      aggregate.memberKinds.add(edge.type);
      continue;
    }

    emitLeafEdge(edge);
  }

  for (const aggregate of aggregates.values()) {
    const related = isEndpointRelatedToSelection(
      aggregate.from,
      aggregate.to,
      aggregate.from,
      aggregate.to,
    );
    const color = colorForContainer(aggregate.from);
    if (related) markRelatedContainers(aggregate.from, aggregate.to);
    const shouldExpand =
      options.collapseEdges === false ||
      expandedEdgeSet.has(aggregate.id) ||
      expandAllAggregates ||
      selectedContainerIds.has(aggregate.from) ||
      selectedContainerIds.has(aggregate.to) ||
      aggregate.members.some((member) => selectedLeafIds.has(member.from) || selectedLeafIds.has(member.to));

    if (shouldExpand) {
      for (const edge of aggregate.members) {
        emitLeafEdge(edge, aggregate.id);
      }
    }

    const showAggregate = options.collapseEdges !== false || shouldExpand;
    if (showAggregate) sceneEdges.push({
      id: aggregate.id,
      type: 'aggregated' as unknown as Edge['type'],
      from: aggregate.from,
      to: aggregate.to,
      attrs: {
        edgeTier: 'data',
        infraEdgeKind: 'aggregated',
        routingType: 'orthogonal',
        aggregated: true,
        aggregateCount: aggregate.members.length,
        memberEdgeIds: aggregate.members.map((member) => member.id),
        memberKinds: [...aggregate.memberKinds],
        label: aggregate.members.length > 1 ? `x${aggregate.members.length}` : undefined,
        color,
        opacity: related ? 0.98 : 0.16,
        width: related
          ? aggregateStrokeWidth(aggregate.members.length)
          : Math.max(1.25, aggregateStrokeWidth(aggregate.members.length) - 2),
        sourceContainerId: aggregate.from,
        targetContainerId: aggregate.to,
      } as Record<string, unknown>,
    } as Edge);
  }

  if (selectedLeafContainerIds.size > 0) {
    for (const containerId of selectedLeafContainerIds) relatedContainerIds.add(containerId);
  }
  if (expandAllAggregates) {
    for (const frame of frames) relatedContainerIds.add(frame.id);
  }
  if (hasConnectorSelection) {
    for (const node of sceneNodes) {
      if (node.type !== 'frame') continue;
      const isSelected = selectedContainerIds.has(node.id) || selectedLeafContainerIds.has(node.id);
      const isRelated = relatedContainerIds.has(node.id) || node.id === `frame:${vpcId}`;
      if (!isSelected && !isRelated) {
        node.strokeColor = '#cbd5e1';
        node.fillColor = 'rgba(148,163,184,0.035)';
        node.strokeWidth = 1.25;
        continue;
      }
      const accent = colorForContainer(node.id);
      node.strokeColor = accent;
      node.fillColor = fillForColor(accent).replace('0.07)', isSelected ? '0.13)' : '0.085)');
      node.strokeWidth = isSelected ? 2.5 : 2;
      node.attrs = {
        ...(node.attrs ?? {}),
        connectorFocus: isSelected ? 'selected' : 'related',
      } as Record<string, unknown>;
    }
  }

  routeCompassExplorerEdges(sceneNodes, sceneEdges);

  return {
    version: 1,
    nodes: sceneNodes,
    edges: sceneEdges,
    meta: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

function routeCompassExplorerEdges(sceneNodes: Node[], sceneEdges: Edge[]): void {
  if (sceneEdges.length === 0) return;

  try {
    const obstacles = sceneNodes
      .map((node) => {
        const width = node.w ?? node.width ?? 0;
        const height = node.h ?? node.height ?? 0;
        if (!width || !height) return null;
        const isFrame = node.type === 'frame';
        return {
          id: node.id,
          rect: { x: node.x, y: node.y, width, height },
          blocking: true,
          segmentOnly: isFrame,
        };
      })
      .filter((obstacle): obstacle is {
        id: string;
        rect: { x: number; y: number; width: number; height: number };
        blocking: boolean;
        segmentOnly: boolean;
      } => obstacle !== null);

    const routes = routeOrthogonalEdges(
      obstacles,
      sceneEdges.map((edge) => ({
        id: edge.id,
        fromObstacleId: edge.from,
        toObstacleId: edge.to,
        fromSide: edge.fromAnchor,
        toSide: edge.toAnchor,
      })),
      {
        clearance: 24,
        turnPenalty: 40,
        dropOnFailure: true,
        laneSpacing: 16,
        debug: isCompassRouterDebugEnabled(),
      },
    );

    const droppedEdgeIds = new Set<string>();
    for (const edge of sceneEdges) {
      const routed = routes.get(edge.id);
      if (!routed || routed.points.length < 4) {
        droppedEdgeIds.add(edge.id);
        continue;
      }
      edge.fromAnchor = routed.fromSide;
      edge.toAnchor = routed.toSide;
      const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
      attrs.layoutPoints = routed.points;
      attrs.layoutSource = 'compass-explorer';
      edge.attrs = attrs;
    }

    if (droppedEdgeIds.size > 0) {
      for (let i = sceneEdges.length - 1; i >= 0; i--) {
        if (droppedEdgeIds.has(sceneEdges[i].id)) sceneEdges.splice(i, 1);
      }
    }
  } catch (err) {
    console.error('[compass:router] focused VPC resource routing failed', err);
  }
}

function makeCompassFrame(args: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  title: string;
  resourceType: string;
  containerId: string;
  stroke: string;
  fill: string;
  zIndex: number;
  extraAttrs?: Record<string, unknown>;
}): Node {
  return {
    id: args.id,
    type: 'frame',
    zIndex: args.zIndex,
    x: args.x,
    y: args.y,
    w: args.width,
    h: args.height,
    width: args.width,
    height: args.height,
    name: args.title,
    strokeColor: args.stroke,
    fillColor: args.fill,
    strokeWidth: 1.5,
    attrs: {
      frameType: 'basic',
      title: args.title,
      resourceType: args.resourceType,
      containerId: args.containerId,
      importedFrom: 'compass-explorer-frame',
      ...args.extraAttrs,
    } as Record<string, unknown>,
  } as Node;
}

/**
 * Pick a card size for a bubble. Width scales with resource count so
 * a 146-resource VPC earns more room than a 2-resource one (the
 * viewer's first glance already conveys scale). Height is driven by
 * the tier-band layout — each non-empty tier occupies its own row,
 * and crowded tiers wrap.
 *
 * Kept in lockstep with `CompassContainerCard` so the rendered card
 * hugs its bounds with no blank padding or overflow.
 */
function bubbleDimensions(
  b: BubbleNode,
  isExpanded: boolean,
  hasFindings: boolean,
): { width: number; height: number } {
  const count = Math.max(1, b.interior?.length ?? b.resourceCount);

  // Width scales with sqrt(count) so the ratio between a tiny VPC and
  // the largest one is visible but bounded. Expanded mode forces a
  // wider card so the 14-per-row readable grid fits.
  const MIN_W = 320;
  const MAX_W = 520;
  const width = isExpanded
    ? Math.max(380, Math.min(MAX_W, MIN_W + Math.sqrt(count) * 16))
    : Math.round(Math.max(MIN_W, Math.min(MAX_W, MIN_W + Math.sqrt(count) * 14)));

  // Height = header + sum(band heights). Tier grouping is deterministic
  // from `interior`, so doing it here lets us size the card exactly.
  const bands = groupByTier(b.interior ?? []);
  const dotsPerRow = cardDotsPerRow(width);
  let bodyH = 0;
  for (const band of bands) {
    bodyH += cardBandHeight(band.dots.length, dotsPerRow) + CARD_BAND_GAP;
  }
  if (bands.length === 0) bodyH = 28;
  const height = cardHeaderHeight(hasFindings) + 8 + bodyH + 44;
  return {
    width: Math.round(width),
    height: Math.round(Math.max(150, height)),
  };
}
